// ============================================
// 오리 MBTI 시스템 (DUCKTI)
// 플레이어의 선택에 따라 MBTI가 결정되고 엔딩에 영향
// ============================================

export interface DuckMBTI {
    // E/I: 외향/내향 - 외교적 선택 vs 고립적 선택
    extroversion: number;  // -100 (I) ~ +100 (E)

    // S/N: 감각/직관 - 현실적 선택 vs 모험적 선택
    sensing: number;       // -100 (N) ~ +100 (S)

    // T/F: 사고/감정 - 논리적 선택 vs 감성적 선택
    thinking: number;      // -100 (F) ~ +100 (T)

    // J/P: 판단/인식 - 계획적 선택 vs 즉흥적 선택
    judging: number;       // -100 (P) ~ +100 (J)
}

export type MBTIType =
    | 'ESTJ' | 'ESTP' | 'ESFJ' | 'ESFP'
    | 'ENTJ' | 'ENTP' | 'ENFJ' | 'ENFP'
    | 'ISTJ' | 'ISTP' | 'ISFJ' | 'ISFP'
    | 'INTJ' | 'INTP' | 'INFJ' | 'INFP';

export interface DuckPersonality {
    type: MBTIType;
    name: string;
    icon: string;
    description: string;
    strengths: string[];
    weaknesses: string[];
    compatibleEndings: string[];
    bonusEffects: {
        moneyMultiplier: number;
        happinessMultiplier: number;
        eventModifier: string[];
    };
}

// MBTI 성격 유형별 오리 정의
export const DUCK_PERSONALITIES: Record<MBTIType, DuckPersonality> = {
    // 분석가형
    INTJ: {
        type: 'INTJ',
        name: '전략가 오리',
        icon: '🦆🧠',
        description: '모든 것을 계획하는 천재 오리. 연못의 마스터 플래너.',
        strengths: ['장기 계획 수립', '위기 대처 능력', '자원 효율성'],
        weaknesses: ['사회성 부족', '융통성 부족'],
        compatibleEndings: ['CONQUEST', 'LEGEND', 'TRUE_ENDING'],
        bonusEffects: {
            moneyMultiplier: 1.2,
            happinessMultiplier: 0.9,
            eventModifier: ['strategy_bonus', 'crisis_resist']
        }
    },
    INTP: {
        type: 'INTP',
        name: '발명가 오리',
        icon: '🦆💡',
        description: '호기심 많은 탐구자. 새로운 것을 발견하는 것이 삶의 낙.',
        strengths: ['창의적 해결책', '지식 축적', '분석력'],
        weaknesses: ['실행력 부족', '현실 감각 부족'],
        compatibleEndings: ['SKY_KINGDOM', 'SECRET_DUCK', 'ASCENSION'],
        bonusEffects: {
            moneyMultiplier: 1.0,
            happinessMultiplier: 1.0,
            eventModifier: ['discovery_bonus', 'research_bonus']
        }
    },
    ENTJ: {
        type: 'ENTJ',
        name: '지휘관 오리',
        icon: '🦆👑',
        description: '타고난 리더. 모든 오리들이 따르는 카리스마의 소유자.',
        strengths: ['리더십', '결단력', '조직 관리'],
        weaknesses: ['독단적', '감정 표현 서툼'],
        compatibleEndings: ['CONQUEST', 'MERCHANT_EMPIRE', 'GOLDEN_DUCK'],
        bonusEffects: {
            moneyMultiplier: 1.3,
            happinessMultiplier: 0.85,
            eventModifier: ['leadership_bonus', 'army_bonus']
        }
    },
    ENTP: {
        type: 'ENTP',
        name: '변론가 오리',
        icon: '🦆💬',
        description: '말빨 좋은 논쟁꾼. 어떤 상황도 말로 해결한다.',
        strengths: ['협상력', '임기응변', '아이디어'],
        weaknesses: ['지구력 부족', '규칙 무시'],
        compatibleEndings: ['FOX_ALLIANCE', 'MERCHANT_EMPIRE', 'MIGRATION'],
        bonusEffects: {
            moneyMultiplier: 1.1,
            happinessMultiplier: 1.1,
            eventModifier: ['diplomacy_bonus', 'trade_bonus']
        }
    },

    // 외교관형
    INFJ: {
        type: 'INFJ',
        name: '예언자 오리',
        icon: '🦆🔮',
        description: '신비로운 통찰력의 소유자. 미래를 내다보는 현자.',
        strengths: ['직관력', '공감 능력', '비전 제시'],
        weaknesses: ['현실 회피', '완벽주의'],
        compatibleEndings: ['PEACE', 'ASCENSION', 'TRUE_ENDING'],
        bonusEffects: {
            moneyMultiplier: 0.9,
            happinessMultiplier: 1.3,
            eventModifier: ['prophecy_bonus', 'chain_bonus']
        }
    },
    INFP: {
        type: 'INFP',
        name: '몽상가 오리',
        icon: '🦆🌈',
        description: '순수한 영혼의 낭만주의자. 모든 오리를 사랑한다.',
        strengths: ['창의성', '이상주의', '적응력'],
        weaknesses: ['비현실적', '우유부단'],
        compatibleEndings: ['PEACE', 'SKY_KINGDOM', 'MIGRATION'],
        bonusEffects: {
            moneyMultiplier: 0.8,
            happinessMultiplier: 1.4,
            eventModifier: ['happiness_bonus', 'art_bonus']
        }
    },
    ENFJ: {
        type: 'ENFJ',
        name: '선동가 오리',
        icon: '🦆📢',
        description: '대중을 이끄는 카리스마. 모든 오리의 마음을 얻는다.',
        strengths: ['영향력', '팀워크', '동기 부여'],
        weaknesses: ['지나친 이타심', '자기 희생'],
        compatibleEndings: ['PEACE', 'LEGEND', 'GOLDEN_DUCK'],
        bonusEffects: {
            moneyMultiplier: 1.0,
            happinessMultiplier: 1.3,
            eventModifier: ['population_bonus', 'morale_bonus']
        }
    },
    ENFP: {
        type: 'ENFP',
        name: '탐험가 오리',
        icon: '🦆🌟',
        description: '자유로운 영혼의 모험가. 새로운 세상을 꿈꾼다.',
        strengths: ['열정', '사교성', '적응력'],
        weaknesses: ['집중력 부족', '약속 불이행'],
        compatibleEndings: ['MIGRATION', 'SKY_KINGDOM', 'SEA_KINGDOM'],
        bonusEffects: {
            moneyMultiplier: 0.9,
            happinessMultiplier: 1.2,
            eventModifier: ['exploration_bonus', 'friend_bonus']
        }
    },

    // 관리자형
    ISTJ: {
        type: 'ISTJ',
        name: '관리자 오리',
        icon: '🦆📋',
        description: '성실하고 책임감 있는 실무자. 왕국의 기둥.',
        strengths: ['신뢰성', '조직력', '인내심'],
        weaknesses: ['융통성 부족', '변화 거부'],
        compatibleEndings: ['GOLDEN_DUCK', 'PEACE', 'CONQUEST'],
        bonusEffects: {
            moneyMultiplier: 1.2,
            happinessMultiplier: 1.0,
            eventModifier: ['building_bonus', 'stability_bonus']
        }
    },
    ISFJ: {
        type: 'ISFJ',
        name: '수호자 오리',
        icon: '🦆🛡️',
        description: '헌신적인 보호자. 가족과 친구를 위해 모든 것을 바친다.',
        strengths: ['헌신', '관찰력', '배려심'],
        weaknesses: ['자기 주장 부족', '변화 두려움'],
        compatibleEndings: ['PEACE', 'LEGEND', 'TRUE_ENDING'],
        bonusEffects: {
            moneyMultiplier: 1.0,
            happinessMultiplier: 1.2,
            eventModifier: ['defense_bonus', 'healing_bonus']
        }
    },
    ESTJ: {
        type: 'ESTJ',
        name: '사령관 오리',
        icon: '🦆⚔️',
        description: '질서를 중시하는 지도자. 법과 규칙의 수호자.',
        strengths: ['조직 관리', '결단력', '책임감'],
        weaknesses: ['권위적', '고집'],
        compatibleEndings: ['CONQUEST', 'GOLDEN_DUCK', 'MERCHANT_EMPIRE'],
        bonusEffects: {
            moneyMultiplier: 1.25,
            happinessMultiplier: 0.9,
            eventModifier: ['army_bonus', 'order_bonus']
        }
    },
    ESFJ: {
        type: 'ESFJ',
        name: '집정관 오리',
        icon: '🦆🤝',
        description: '사교적인 조화의 수호자. 모두를 하나로 묶는다.',
        strengths: ['사교성', '협동심', '세심함'],
        weaknesses: ['비판에 민감', '의존적'],
        compatibleEndings: ['PEACE', 'FOX_ALLIANCE', 'GOLDEN_DUCK'],
        bonusEffects: {
            moneyMultiplier: 1.1,
            happinessMultiplier: 1.2,
            eventModifier: ['diplomacy_bonus', 'festival_bonus']
        }
    },

    // 탐험가형
    ISTP: {
        type: 'ISTP',
        name: '장인 오리',
        icon: '🦆🔧',
        description: '뛰어난 손재주의 기술자. 무엇이든 만들어낸다.',
        strengths: ['기술력', '문제 해결', '침착함'],
        weaknesses: ['감정 표현 서툼', '약속 가벼움'],
        compatibleEndings: ['GOLDEN_DUCK', 'SEA_KINGDOM', 'SECRET_DUCK'],
        bonusEffects: {
            moneyMultiplier: 1.15,
            happinessMultiplier: 1.0,
            eventModifier: ['craft_bonus', 'repair_bonus']
        }
    },
    ISFP: {
        type: 'ISFP',
        name: '예술가 오리',
        icon: '🦆🎨',
        description: '아름다움을 추구하는 자유로운 영혼.',
        strengths: ['예술성', '공감력', '유연성'],
        weaknesses: ['장기 계획 부족', '경쟁 회피'],
        compatibleEndings: ['PEACE', 'MIGRATION', 'ASCENSION'],
        bonusEffects: {
            moneyMultiplier: 0.9,
            happinessMultiplier: 1.3,
            eventModifier: ['art_bonus', 'nature_bonus']
        }
    },
    ESTP: {
        type: 'ESTP',
        name: '사업가 오리',
        icon: '🦆💰',
        description: '기회를 포착하는 눈. 위험을 두려워하지 않는다.',
        strengths: ['순발력', '협상력', '대담함'],
        weaknesses: ['인내심 부족', '규칙 무시'],
        compatibleEndings: ['MERCHANT_EMPIRE', 'CONQUEST', 'FOX_ALLIANCE'],
        bonusEffects: {
            moneyMultiplier: 1.4,
            happinessMultiplier: 0.9,
            eventModifier: ['trade_bonus', 'gamble_bonus']
        }
    },
    ESFP: {
        type: 'ESFP',
        name: '연예인 오리',
        icon: '🦆🎭',
        description: '파티의 중심. 모든 이의 시선을 사로잡는다.',
        strengths: ['사교성', '낙관성', '적응력'],
        weaknesses: ['장기 계획 부족', '충동적'],
        compatibleEndings: ['PEACE', 'GOLDEN_DUCK', 'MIGRATION'],
        bonusEffects: {
            moneyMultiplier: 1.0,
            happinessMultiplier: 1.4,
            eventModifier: ['festival_bonus', 'morale_bonus']
        }
    }
};

// 선택에 따른 MBTI 점수 변화 태그
export interface MBTIModifier {
    E?: number;  // 외향 (+) / 내향 (-)
    S?: number;  // 감각 (+) / 직관 (-)
    T?: number;  // 사고 (+) / 감정 (-)
    J?: number;  // 판단 (+) / 인식 (-)
}

// 초기 MBTI 상태
export const INITIAL_MBTI: DuckMBTI = {
    extroversion: 0,
    sensing: 0,
    thinking: 0,
    judging: 0
};

// MBTI 점수를 유형으로 변환
export function calculateMBTIType(mbti: DuckMBTI): MBTIType {
    const e = mbti.extroversion >= 0 ? 'E' : 'I';
    const s = mbti.sensing >= 0 ? 'S' : 'N';
    const t = mbti.thinking >= 0 ? 'T' : 'F';
    const j = mbti.judging >= 0 ? 'J' : 'P';
    return `${e}${s}${t}${j}` as MBTIType;
}

// MBTI 점수 업데이트
export function updateMBTI(current: DuckMBTI, modifier: MBTIModifier): DuckMBTI {
    return {
        extroversion: clamp(current.extroversion + (modifier.E || 0), -100, 100),
        sensing: clamp(current.sensing + (modifier.S || 0), -100, 100),
        thinking: clamp(current.thinking + (modifier.T || 0), -100, 100),
        judging: clamp(current.judging + (modifier.J || 0), -100, 100)
    };
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

// MBTI 성향 강도 (0-100%)
export function getMBTIStrength(mbti: DuckMBTI): {
    EI: number;
    SN: number;
    TF: number;
    JP: number;
} {
    return {
        EI: Math.abs(mbti.extroversion),
        SN: Math.abs(mbti.sensing),
        TF: Math.abs(mbti.thinking),
        JP: Math.abs(mbti.judging)
    };
}

// MBTI에 따른 엔딩 호환성 체크
export function getEndingCompatibility(mbti: DuckMBTI, endingType: string): number {
    const type = calculateMBTIType(mbti);
    const personality = DUCK_PERSONALITIES[type];

    if (personality.compatibleEndings.includes(endingType)) {
        return 1.5; // 50% 보너스
    }
    return 1.0;
}

// MBTI 설명 텍스트 생성
export function getMBTIDescription(mbti: DuckMBTI): string {
    const type = calculateMBTIType(mbti);
    const personality = DUCK_PERSONALITIES[type];
    const strength = getMBTIStrength(mbti);

    const avgStrength = (strength.EI + strength.SN + strength.TF + strength.JP) / 4;

    let certainty = '';
    if (avgStrength < 20) {
        certainty = '아직 성격이 형성 중입니다...';
    } else if (avgStrength < 50) {
        certainty = '성격의 윤곽이 보이기 시작합니다.';
    } else if (avgStrength < 80) {
        certainty = '뚜렷한 성격이 드러나고 있습니다.';
    } else {
        certainty = '확고한 성격이 완성되었습니다!';
    }

    return `${personality.name} (${type})\n${certainty}\n\n${personality.description}`;
}
